import React, {useEffect, useMemo, useState} from "react";

interface ElapsedBadgeProps {
    label: string;
    startAt: string;
    frozen?: boolean;
    freezeAt?: string | null;
    className?: string;
}

export function ElapsedBadge({
                                 label,
                                 startAt,
                                 frozen = false,
                                 freezeAt = null,
                                 className = "",
                             }: ElapsedBadgeProps): JSX.Element {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (frozen) return undefined;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [frozen]);

    const value = useMemo(() => {
        const start = Date.parse(startAt);
        if (!Number.isFinite(start)) return "--:--:--";
        const end = frozen && freezeAt ? Date.parse(freezeAt) : now;
        if (!Number.isFinite(end)) return "--:--:--";
        return formatElapsed(Math.max(0, end - start));
    }, [freezeAt, frozen, now, startAt]);

    return (
        <span
            className={`elapsed-badge ${frozen ? "frozen" : "live"} ${className}`.trim()}
            title={`${label}: ${value}`}
        >
            <span className="elapsed-label">{label}</span>
            <span className="elapsed-value">{value}</span>
        </span>
    );
}

function formatElapsed(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const clock = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    return days > 0 ? `${days}d ${clock}` : clock;
}

function pad(value: number): string {
    return String(value).padStart(2, "0");
}
